import { FC, MouseEvent, useState } from 'react';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import Popover from '@material-ui/core/Popover';
import { makeStyles, createStyles } from '@material-ui/core/styles';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';

import Checkbox from 'src/components/Checkbox';
import { UserPermissions } from 'src/types';
import {
  DEFAULT_USER_PERMISSIONS,
  USER_PERMISSION_OPTIONS,
} from 'src/utils/permissions';

interface AdditionalPermissionsProps {
  value?: Partial<UserPermissions>;
  disabled?: boolean;
  saving?: boolean;
  onSave: (next: UserPermissions) => Promise<void> | void;
}

const toPermissions = (value?: Partial<UserPermissions>): UserPermissions => ({
  ...DEFAULT_USER_PERMISSIONS,
  ...value,
});

const countCheckedPermissions = (permissions: UserPermissions): number =>
  USER_PERMISSION_OPTIONS.reduce((count, { key, inverted }) => {
    const checked = inverted ? !permissions[key] : permissions[key];
    return count + (checked ? 1 : 0);
  }, 0);

const AdditionalPermissions: FC<AdditionalPermissionsProps> = ({
  value,
  disabled = false,
  saving = false,
  onSave,
}) => {
  const classes = useStyles();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const savedPermissions = toPermissions(value);
  const [draft, setDraft] = useState<UserPermissions>(savedPermissions);
  const open = Boolean(anchorEl);
  const displayPermissions = open ? draft : savedPermissions;
  const checkedCount = countCheckedPermissions(displayPermissions);
  const totalCount = USER_PERMISSION_OPTIONS.length;
  const isDirty = USER_PERMISSION_OPTIONS.some(
    ({ key }) => draft[key] !== savedPermissions[key]
  );

  const handleOpen = (event: MouseEvent<HTMLElement>) => {
    // Seed draft only when opening so in-flight saves don't reset checkboxes.
    setDraft(toPermissions(value));
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    if (saving) return;
    setAnchorEl(null);
  };

  const handleSave = async () => {
    await onSave(draft);
    setAnchorEl(null);
  };

  return (
    <>
      <Button
        size='small'
        variant='outlined'
        color='primary'
        onClick={handleOpen}
        endIcon={<KeyboardArrowDownIcon />}
        className={classes.trigger}
        disabled={saving}
      >
        {checkedCount} of {totalCount} selected
      </Button>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <Box className={classes.popover}>
          {USER_PERMISSION_OPTIONS.map(({ key, label, inverted }) => {
            const checked = inverted ? !draft[key] : draft[key];

            return (
              <Checkbox
                key={key}
                name={key}
                label={label}
                checked={checked}
                disabled={disabled || saving}
                noPadding
                className={classes.checkbox}
                onChange={(nextChecked) => {
                  setDraft((prev) => ({
                    ...prev,
                    [key]: inverted ? !nextChecked : nextChecked,
                  }));
                }}
              />
            );
          })}
          {!disabled && (
            <Box className={classes.actions}>
              <Button
                size='small'
                onClick={handleClose}
                disabled={saving}
                className={classes.actionButton}
              >
                Cancel
              </Button>
              <Button
                size='small'
                color='primary'
                variant='contained'
                disableElevation
                onClick={handleSave}
                disabled={!isDirty || saving}
                className={classes.actionButton}
              >
                {saving ? 'Saving...' : 'Save'}
              </Button>
            </Box>
          )}
        </Box>
      </Popover>
    </>
  );
};

const useStyles = makeStyles(() =>
  createStyles({
    trigger: {
      textTransform: 'none',
      whiteSpace: 'nowrap',
      fontSize: 13,
      fontWeight: 500,
    },
    popover: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-start',
      padding: '8px 12px 12px',
      minWidth: 260,
    },
    checkbox: {
      height: 28,
      '& .MuiTypography-root': {
        fontSize: 13,
      },
      '& .MuiCheckbox-root': {
        padding: 4,
      },
    },
    actions: {
      display: 'flex',
      justifyContent: 'flex-end',
      gap: 8,
      width: '100%',
      marginTop: 12,
      paddingTop: 8,
      borderTop: '1px solid #E5E5E5',
    },
    actionButton: {
      textTransform: 'none',
      fontSize: 13,
    },
  })
);

export default AdditionalPermissions;
